import React, { useMemo } from 'react';
import _ from 'lodash';
import { ContentLayoutModule } from '../modules/ContentLayoutModule';

export default function LayoutRenderer({ layout, xrayMode = false, ...rest }) {
  const { conditions } = rest;

  const findCompatibleSibling = (element, layout, rows, index) => {
    if (index >= rows.length) {
      return []; // Terminate recursion when there are no more rows to search
    }

    const nextRow = rows[index];
    const compatibleSibling = layout.find(item => item.posY === nextRow && item.posX === element.posX && item.width === element.width);
    // || (item.contentModule.type === "SPACE" && Math.abs(item.posY - nextRow) <= 100));
    if (compatibleSibling) {
      return [compatibleSibling, ...findCompatibleSibling(compatibleSibling, layout, rows, index + 1)];
    }

    return [];
  };

  const elements = useMemo(() => {
    // Prevent multiple renders of the same element if some are have a column compatibility
    const added = [];
    const rows = [...new Set(_.sortBy(layout, ['posY']).map(item => item.posY))];
    const renderedElements = [];

    rows.forEach((r, index) => {
      const rowElements = [];
      const nextRow = rows[index + 1];
      const maxHeightInRow = Math.max(...layout.filter(item => item.posY === r).map(item => item.height));

      let elementsInRow = layout.filter(item => !added.includes(item.contentModuleId) && item.posY === r);

      const allElementsHidden = elementsInRow.every(
        item => item.renderHeight === 1 || (conditions[item.contentLayoutModuleId] && conditions[item.contentLayoutModuleId].hidden == true)
      );

      // Add all the columns in the row
      if (elementsInRow.length > 0 && !allElementsHidden) {
        const cols = [...new Set(elementsInRow.map(item => item.posX))];

        added.push(...elementsInRow.map(item => item.contentModuleId));

        cols?.forEach(c => {
          const colElements = [];
          const elementsInColumn = elementsInRow.filter(item => item.posX === c);
          const colWidth = Math.max(...elementsInRow.filter(item => item.posX === c)?.map(item => item.width));

          _.sortBy(elementsInColumn, ['posY'])?.forEach(element => {
            added.push(element.contentModuleId);
            const compatibleSiblings = findCompatibleSibling(element, layout, rows, index + 1);

            colElements.push(
              <div
                key={element.contentLayoutModuleId.toString()}
                id={element.posY}
                style={{ height: compatibleSiblings.length > 0 ? 'auto' : '100%', border: xrayMode ? '1px solid red' : undefined }}
              >
                <ContentLayoutModule
                  {...{
                    ...element,
                    ...element.contentModule,
                    ...rest,
                  }}
                />
              </div>
            );

            if (compatibleSiblings.length > 0) {
              compatibleSiblings.forEach(compatibleSibling => {
                added.push(compatibleSibling.contentModuleId);
                colElements.push(
                  <div
                    key={compatibleSibling.contentLayoutModuleId.toString()}
                    id={compatibleSibling.posY}
                    style={{ height: '100%', border: xrayMode ? '1px solid red' : undefined }}
                  >
                    <ContentLayoutModule
                      {...{
                        ...compatibleSibling,
                        ...compatibleSibling.contentModule,
                        ...rest,
                      }}
                    />
                  </div>
                );
              });
            }
          });

          rowElements.push(
            <div
              key={r.toString() + c.toString()}
              className="cms-col"
              style={{
                width: '100%',
                gridColumnStart: c + 1,
                gridColumnEnd: c + colWidth + 1,
                border: xrayMode ? '1px solid green' : undefined,
              }}
            >
              {colElements}
            </div>
          );
        });

        renderedElements.push(
          <div className="cms-row" id={maxHeightInRow} key={r.toString()} style={{ border: xrayMode ? '1px solid lightblue' : undefined }}>
            {rowElements}
          </div>
        );
      }
    });

    return renderedElements;
  }, [layout, rest.ordersLoaded]);

  return <div style={{ position: 'relative' }}>{elements}</div>;
}
